import type { FileStoreAdapter, StoredFile } from '@healthshare/shared/platform/fileStore'

const DB_NAME = 'healthshare-file-store'
const DB_VERSION = 1
const STORE_NAME = 'files'

type StoredRecord = {
  key: string
  bytes: ArrayBuffer
  contentType: string
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function runTransaction<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode)
    const request = work(tx.objectStore(STORE_NAME))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * The web app's FileStoreAdapter, backed by IndexedDB rather than the Origin Private File
 * System: IndexedDB has universal browser support and no worker-thread constraints, and
 * it's more than enough for whole-file put/get/delete/list over receipt-sized documents.
 */
export function createWebFileStore(): FileStoreAdapter {
  return {
    async put(key, bytes, contentType) {
      const db = await openDb()
      try {
        await runTransaction(db, 'readwrite', (store) =>
          store.put({ key, bytes, contentType } satisfies StoredRecord)
        )
      } finally {
        db.close()
      }
    },

    async get(key): Promise<StoredFile | null> {
      const db = await openDb()
      try {
        const record = await runTransaction<StoredRecord | undefined>(db, 'readonly', (store) => store.get(key))
        return record ? { bytes: record.bytes, contentType: record.contentType } : null
      } finally {
        db.close()
      }
    },

    async delete(key) {
      const db = await openDb()
      try {
        await runTransaction(db, 'readwrite', (store) => store.delete(key))
      } finally {
        db.close()
      }
    },

    async list() {
      const db = await openDb()
      try {
        const keys = await runTransaction<IDBValidKey[]>(db, 'readonly', (store) => store.getAllKeys())
        return keys.map((key) => String(key))
      } finally {
        db.close()
      }
    },
  }
}

export const webFileStore = createWebFileStore()
